import { useEffect, useRef, useState } from "react";

interface Options {
  threshold?: number;
  delta?: number;
}

/**
 * Returns `hidden = true` when the user scrolls down past `threshold`,
 * `false` when they scroll up or are near the top of the page.
 */
export function useHideOnScroll({ threshold = 80, delta = 5 }: Options = {}) {
  const [hidden, setHidden] = useState(false);
  const lastY = useRef(0);
  const ticking = useRef(false);

  useEffect(() => {
    lastY.current = window.scrollY;

    const update = () => {
      const y = window.scrollY;
      const diff = y - lastY.current;

      if (Math.abs(diff) < delta) {
        ticking.current = false;
        return;
      }

      if (y <= threshold) {
        setHidden(false);
      } else if (diff > 0) {
        setHidden(true);
      } else {
        setHidden(false);
      }

      lastY.current = y;
      ticking.current = false;
    };

    const onScroll = () => {
      if (!ticking.current) {
        ticking.current = true;
        requestAnimationFrame(update);
      }
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [threshold, delta]);

  return hidden;
}
